import { EventEmitter } from '@timing71/common';
import * as SignalR from '@microsoft/signalr';

import { createHTTPClient, createWebsocketConstructor } from './signalr.js';
import { parseTime } from '../utils.js';
import { getRaceControlMessage } from './translate.js';

export class Client extends EventEmitter {
  constructor(eventID, connectionService) {
    super();
    this.eventID = eventID;
    this.connectionService = connectionService;

    this._initTimeout = null;
    this._statsTimeout = null;
    this._weatherTimeout = null;

    this._sessionStickCookie = null;

    this.start = this.start.bind(this);
    this._updateState = this._updateState.bind(this);
    this._updateStartTime = this._updateStartTime.bind(this);
    this._updateWeather = this._updateWeather.bind(this);

    this._state = {
      bestSectors: {},
      currentSectors: {},
      lastSeenSectors: {},
      weather: {}
    };
  }

  _updateState(newState={}) {
    this._state = {
      ...this._state,
      ...newState
    };
    this.emit('update', this._state);
  }

  start() {
    this.connectionService.fetch(
      `https://livetiming.tsl-timing.com/results/api/sessions/${this.eventID}/active`,
      {
        headers: {
          "X-Tsl-Event": this.eventID,
        },
        returnHeaders: true
      }
    ).then(
      ([activeSession, headers]) => {

        if (headers) {
          const setCookieHeader = headers['set-cookie'];
          if (setCookieHeader) {
            const stick = setCookieHeader.match(/(?<=stick=)[0-9a-z\.|]+(?=;)/);
            if (stick) {
              this._sessionStickCookie = stick[0];
            }
          }
        }

        if (activeSession) {
          this._updateState({
            ...JSON.parse(activeSession),
            stateUpdated: Date.now()
          });
          this.emit('sessionChange', this._state);

          this._updateStartTime();
          this._updateWeather();

          const connection = new SignalR.HubConnectionBuilder()
            .withUrl(
              'https://livetiming.tsl-timing.com/results/live',
              {
                httpClient: createHTTPClient(this.connectionService, this._sessionStickCookie),
                WebSocket: createWebsocketConstructor(this.connectionService, this._sessionStickCookie)
              }
            )
            .withAutomaticReconnect()
            .build();

          connection.on('sessionUpdated', (state) => {
            const newSession = state.id !== this._state.id;
            this._updateState({
              ...state,
              stateUpdated: Date.now(),
              classification: newSession ? [] : this._state.classification,
              actualStart: newSession ? null : this._state.actualStart
            });
            if (newSession) {
              this.emit('sessionChange', this._state);
              this._updateStartTime();
            }
          });

          connection.on('resultUpdated', (update) => {
            // {"id":"853c69b3-d999-6597-c9fb-3fadf20fe723","no":"144","name":"GODDARD / HAND / HOLLINGS (PRO)","team":"RNR","primaryClass":"BEC","subClass":"C","vehicle":"Ferrari","sponsor":"","nationality":"","manufacturer":"","result":{"position":7,"pic":1,"posChange":0,"laps":22,"raceTime":"2:50:31.757","fastLapTime":"2:13.970","gap":"13.466","diff":"0.167","pitStops":6},"lastLapTime":"6:50.419","state":0,"currentDriver":{"id":"091cfba5-0a1f-ddd9-3648-31d165d5e645","firstName":"Chris","lastName":"GODDARD","nationality":""}}
            if (!update.result && !update.groupResult) {
              // Remove an entry from the results
              this._updateState({
                classification: this._state.classification.filter(c => c.id !== update.id)
              });
            }
            else {
              const extant = this._state.classification.findIndex(c => c.id === update.id);
              if (extant >= 0) {
                // Update existing entry
                const newClassification = [...this._state.classification];
                newClassification[extant] = { ...newClassification[extant], ...update};
                this._updateState({ classification: newClassification });
              }
              else {
                // Add new entry
                this._updateState({
                  classification: [
                    ...this._state.classification,
                    update
                  ]
                });
              }
            }
          });

          connection.on('competitorIntermediate', (st) => {
            // {"id":"3c167015-7784-ff38-799f-79cde27eda56","sectorKey":"S2Time","time":"1:20.683","timePB":false,"speedPB":true}

            if (!this._state.currentSectors[st.id]) {
              this._state.currentSectors[st.id] = {};
            }
            if (!this._state.bestSectors[st.id]) {
              this._state.bestSectors[st.id] = {};
            }

            const current = this._state.currentSectors[st.id];
            const time = parseTime(st.time);
            current[st.sectorKey] = time;

            if (st.timePB) {
              const best = this._state.bestSectors[st.id];
              best[st.sectorKey] = time;
            }

            this._state.lastSeenSectors[st.id] = st.sectorKey;

            this._updateState();
          });

          connection.on('newSectorBest', (sb) => {
            const sectors = [...this._state.track?.sectors || []];
            const sector = sectors.find(s => s.key === sb.sectorKey);
            if (sector) {
              sector.sessionBest = {
                id: sb.id,
                time: sb.time
              }
            };

            this._updateState({
              track: {
                ...this._state.track,
                sectors
              }
            });
          });

          connection.on('rcMsgReceived', (msg) => {
            if (msg.text) {
              this.emit('raceControlMessage', getRaceControlMessage(msg));
            }
          });

          connection.onreconnected(
            () => {
              connection.send('registerForEvent', this.eventID);
            }
          )

          connection.start().then(
            () => {
              connection.send('registerForEvent', this.eventID);
            }
          );
        }
        else {
          this._initTimeout = setTimeout(this.start, 10000);
        }

      }
    );
  }

  stop() {
    if (this._initTimeout) {
      clearTimeout(this._initTimeout);
      this._initTimeout = null;
    }
    if (this._statsTimeout) {
      clearTimeout(this._statsTimeout);
      this._statsTimeout = null;
    }
    if (this._weatherTimeout) {
      clearTimeout(this._weatherTimeout);
      this._weatherTimeout = null;
    }
  }

  async _updateStartTime() {
    const statsRaw = await this.connectionService.fetch(
      `https://livetiming.tsl-timing.com/results/api/stats/${this._state.id}`,
      {
        headers: {
          "X-Tsl-Event": this.eventID
        }
      }
    );

    if (statsRaw) {
      const stats = JSON.parse(statsRaw);
      if (stats.actualStart) {
        this._updateState({
          actualStart: new Date(stats.actualStart).getTime()
        });
        return;
      }
    }

    // See if we have a start time again in 30 seconds
    this._statsTimeout = setTimeout(
      this._updateStartTime,
      30000
    );
  }

  async _updateWeather() {
    try {
      const raw = await this.connectionService.fetch(
        `https://livetiming.tsl-timing.com/weather/api/${this.eventID}/latest`,
        {
          headers: {
            "X-Tsl-Event": this.eventID
          }
        }
      );

      if (raw) {
        const weather = JSON.parse(raw);
        this._updateState({ weather });
      }
    }
    catch (SyntaxError) {
      // pass
    }
    finally {
      this._weatherTimeout = setTimeout(
        this._updateWeather,
        60 * 1000
      );
    }
  }
}
